import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, inr } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, FunnelChart, Funnel, LabelList } from "recharts";

const COLORS = ["#312E81", "#4338CA", "#6366F1", "#3B82F6", "#60A5FA", "#4F46E5", "#818CF8"];

function BarCard({ title, url }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(url).then(({ data }) => setData(data)); }, [url]);
  if (!data) return <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm h-72"><Loading /></div>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <h3 className="font-heading font-bold text-[#312E81] mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey="count" radius={[0, 6, 6, 0]}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function FunnelCard({ title, url }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(url).then(({ data }) => setData(data.map((d, i) => ({ ...d, fill: COLORS[i % COLORS.length] })))); }, [url]);
  if (!data) return <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm h-72"><Loading /></div>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <h3 className="font-heading font-bold text-[#312E81] mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={280}>
        <FunnelChart>
          <Tooltip />
          <Funnel dataKey="count" data={data} isAnimationActive>
            <LabelList position="right" fill="#334155" stroke="none" dataKey="stage" fontSize={12} />
            <LabelList position="left" fill="#312E81" stroke="none" dataKey="count" fontSize={13} fontWeight={700} />
          </Funnel>
        </FunnelChart>
      </ResponsiveContainer>
    </div>
  );
}

function Financial() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/financial").then(({ data }) => setD(data)).catch(() => setD({})); }, []);
  if (!d) return <Loading />;
  const items = [["Student Payments", d.student_payments]];
  if (d.consultation_gross !== undefined) {
    items.push(["Consultation Gross", d.consultation_gross], ["TDS", d.tds], ["Consultation Outstanding", d.consultation_outstanding]);
  }
  return (
    <div>
      {d.restricted && <div className="text-xs text-slate-400 mb-3">Consultation charge figures are confidential and hidden for your role.</div>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map(([l, v]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="font-heading text-2xl font-extrabold text-[#312E81]">{inr(v || 0)}</div>
            <div className="text-xs text-slate-500 mt-1">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const hideFinancial = ["telecaller", "counselor", "senior_counselor"].includes(user.role);
  return (
    <div>
      <PageHeader title="Reports" subtitle="Quality admission outcomes — not just lead counts" testid="reports-header" />
      <Tabs defaultValue="conversion">
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
          <TabsTrigger value="conversion" data-testid="rep-tab-conversion">Conversion</TabsTrigger>
          <TabsTrigger value="cold" data-testid="rep-tab-cold">Cold-calling</TabsTrigger>
          <TabsTrigger value="leads" data-testid="rep-tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="admissions" data-testid="rep-tab-adm">Admissions</TabsTrigger>
          {!hideFinancial && <TabsTrigger value="financial" data-testid="rep-tab-fin">Financial</TabsTrigger>}
        </TabsList>
        <TabsContent value="conversion" className="mt-4"><FunnelCard title="Lead → Admission Conversion" url="/reports/conversion" /></TabsContent>
        <TabsContent value="cold" className="mt-4"><FunnelCard title="Cold Calling Funnel (Prospect → Lead)" url="/reports/cold-calling-funnel" /></TabsContent>
        <TabsContent value="leads" className="mt-4"><div className="grid lg:grid-cols-2 gap-4"><BarCard title="Leads by Source" url="/reports/leads-by-source" /><BarCard title="Leads by Course" url="/reports/leads-by-course" /></div></TabsContent>
        <TabsContent value="admissions" className="mt-4"><div className="grid lg:grid-cols-2 gap-4"><BarCard title="Admissions by College" url="/reports/admissions-by-college" /><BarCard title="Admissions by Counselor" url="/reports/admissions-by-counselor" /></div></TabsContent>
        {!hideFinancial && <TabsContent value="financial" className="mt-4"><Financial /></TabsContent>}
      </Tabs>
    </div>
  );
}
